import { PdsWidget, StringBoolean } from './pds-widget';
import { PolymerKeyboardEvent } from './interfaces/polymer-keyboard-event';
import { PolymerMouseEvent } from './interfaces/polymer-mouse-event';
import { HtmlContentSection } from './utils/html-content-sections-engine/html-content-section';
import { pdsLogger } from './pds-logger';
import { HtmlContentSectionsEngine } from './utils/html-content-sections-engine/html-content-sections-engine';
import { NodeGroupingMode } from './utils/html-content-sections-engine/node-grouping-mode';
import { PolymerFocusEvent } from 'powerschool-design-system/pds-library/src/interfaces/polymer-focus-event';

let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('parsed-html-content'), 'pds-parsed-html-content.ts');

interface ParsedHtmlContentModel {
  section: HtmlContentSection;
  sectionIndex: number;
}

export class PdsParsedHtmlContent extends PdsWidget {
  is: 'pds-parsed-html-content';

  // inputs
  id: string;
  dataDebug: StringBoolean = 'false';
  dataEnabled: StringBoolean = 'false';
  dataNodeGroupingMode: keyof typeof NodeGroupingMode = 'Block';
  dataSelectTooltip: string;
  dataDeselectTooltip: string;

  // component properties
  overlayContainerHeight = 0;
  overlayContainerWidth = 0;

  engine: HtmlContentSectionsEngine;

  properties = {
    id: {
      type: String,
      value: ''
    },
    dataDebug: {
      type: String,
      value: 'false'
    },
    dataEnabled: {
      type: String,
      value: 'false',
      observer: 'updateState'
    },
    dataNodeGroupingMode: {
      type: String,
      value: 'Block',
      observer: 'updateState'
    },
    engine: {
      observer: 'updateState',
      value: null
      // see ready() for default value
    },
    dataSelectTooltip: {
      type: String,
      value: null
    },
    dataDeselectTooltip: {
      type: String,
      value: null
    }
  };

  parsedSections: Array<HtmlContentSection> = [];

  private isAttached: boolean | undefined;

  private contentObserver: any;

  // bind refresh overlay to 'this' so that it can be used as a listener
  private resizeOverlay: () => void;

  ready(): void {
    super.ready();
    // default component and template properties
    this.overlayContainerHeight = 0;
    this.overlayContainerWidth = 0;
    this.parsedSections = [];
    this.isAttached = false;

    // default the engine to its most basic state: break on all
    this.engine = new HtmlContentSectionsEngine([HtmlContentSectionsEngine.sectionBreakHeuristics.breakOnAll]);

    // set in ready so that 'this' is the HTML element extended by polymer
    this.resizeOverlay = () => this.refreshOverlay();
  }

  attached(): void {
    super.attached();

    // overlays need to change size when the window is resized
    window.addEventListener('resize', this.resizeOverlay);

    // make sure the component matches the enabled input
    this.updateState();

    // if any content is added or removed, make sure to update the selectable sections
    //  https://polymer-library.polymer-project.org/1.0/docs/devguide/local-dom#observe-nodes
    //  observeNodes will call the observer function anytime this.$.content's descendents are added or removed
    this.contentObserver = this.polymerDom(this.$.content).observeNodes(() => this.updateState());
  }

  detached(): void {
    super.detached();
    // ensure we clean up all resources when detaching the element
    this.disable();

    // clean up the event listener
    window.removeEventListener('resize', this.resizeOverlay);

    // stop watching for changes to the content - the overlay is no longer being shown
    //  https://polymer-library.polymer-project.org/1.0/docs/devguide/local-dom#observe-nodes
    this.polymerDom(this.$.content).unobserveNodes(this.contentObserver);

    // the component is no longer attached
    this.isAttached = false;
  }

  enable(): void {
    if (!this.dataSelectTooltip) {
      console.warn('Missing the select tooltip');
    }

    if (!this.dataDeselectTooltip) {
      console.warn('Missing the deselect tooltip');
    }

    // typecast the data input as an enum key, and double check that it actually is an enum key
    let groupingMode: keyof typeof NodeGroupingMode = this.dataNodeGroupingMode as keyof typeof NodeGroupingMode;
    if (NodeGroupingMode[groupingMode] == null) {
      throw new Error(`Invalid grouping mode: ${groupingMode}`);
    }

    // reset the overlay container size
    this.overlayContainerHeight = this.$.container.offsetHeight;
    this.overlayContainerWidth = this.$.container.offsetWidth;

    // important - do not set the sections until they are completely initialized so that polymer change detection detects all sections
    this.parsedSections = this.engine.parse(
      this.$.container,
      this.overlayContainerWidth,
      this.overlayContainerHeight,
      NodeGroupingMode[groupingMode]);
    log(`${this.parsedSections.length} sections found.`);

    // make sure the enabled input matches the component's state
    this.dataEnabled = 'true';
  }

  disable(): void {
    // make sure the enabled input matches the component's state
    this.dataEnabled = 'false';

    // remove all sections & overlays
    this.parsedSections = [];
  }

  updateState(): void {
    // if we're not attached yet, do nothing - the overlays cannot be shown yet
    if (!this.isAttached) {
      return;
    }

    // enable or disable the component based on the input value
    if (this.dataEnabled === 'true') {
      this.enable();
    } else {
      this.disable();
    }
  }

  sectionOnFocus(event: PolymerMouseEvent<ParsedHtmlContentModel> | PolymerFocusEvent<ParsedHtmlContentModel>): void {
    // set the focused value for this section
    // use polymer mutator so change detection sees the change
    this.set(`parsedSections.${event.model.sectionIndex}.focused`, true);
  }

  sectionOnBlur(event: PolymerMouseEvent<ParsedHtmlContentModel> | PolymerFocusEvent<ParsedHtmlContentModel>): void {
    // unset the focused value for this section
    // use polymer mutator so change detection sees the change
    this.set(`parsedSections.${event.model.sectionIndex}.focused`, false);
  }

  sectionOnClick(event: PolymerMouseEvent<ParsedHtmlContentModel>): void {
    // toggle the selected value for this section
    // use polymer mutator so change detection sees the change
    this.set(`parsedSections.${event.model.sectionIndex}.selected`, !event.model.section.selected);

    // emit section-clicked so other components can respond to the new selection or deselection
    this.dispatchEvent(new Event('section-clicked'));
  }

  // gets the text from each Html Content Section that has been selected by the suer
  getSelectedText(): Array<string> {
    return this.parsedSections
      // filter out any unselected sections
      .filter((section: HtmlContentSection): boolean => section.selected)
      // get the inner text of each element in that section
      .map((section: HtmlContentSection): Array<string> => section.getText())
      // combine each text chunk together into a single array
      .reduce((allText: Array<string>, text: Array<string>): Array<string> => allText.concat(text), []);
  }

  refreshOverlay(): void {
    // set the component property to [] so polymer 'forgets' about the current sections
    let parsedSections = this.parsedSections;
    this.parsedSections = [];

    // polymer does some dom manipulations asynchronously. This forces pending operations to complete.
    this.flushPolymer();

    // reset the overlay container's size now that the overlays have been reset
    this.overlayContainerHeight = this.$.container.offsetHeight;
    this.overlayContainerWidth = this.$.container.offsetWidth;

    // go through each section and make sure its overlay is set properly
    for (let section of parsedSections) {
      section.resetBounds(this.overlayContainerWidth, this.overlayContainerHeight);
    }

    // set the component property back to the full list so polymer sees any changes to the bounds
    this.parsedSections = parsedSections;

    log(`overlay for ${this.id} document resized`);
  }
}
